import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, Copy, Mail, Link2, Ban, ExternalLink, ShieldAlert, Check } from 'lucide-react';

/**
 * Admin modal: create / copy / email / revoke a teacher roster link.
 * Links never expire — they are revoked manually.
 */
export const ShareRosterModal = ({ itemType, item, token, onClose }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(item?.instructorEmail || '');
  const [copiedId, setCopiedId] = useState(null);

  const itemId = String(item?.id);

  const fetchLinks = useCallback(async () => {
    try {
      const res = await fetch(`/api/rosters/links?item_type=${itemType}&item_id=${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setLinks(data.links || []);
    } catch {
      toast.error('Could not load existing roster links');
    } finally {
      setLoading(false);
    }
  }, [itemType, itemId, token]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const createLink = async (sendEmail) => {
    if (sendEmail && !email.trim()) {
      toast.error("Enter the teacher's email address first");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/rosters/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          item_type: itemType,
          item_id: itemId,
          instructor_name: item?.instructor || '',
          instructor_email: email.trim(),
          send_email: !!sendEmail,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create link');
      toast.success(sendEmail ? `Roster link emailed to ${email.trim()}` : 'Roster link created');
      await fetchLinks();
      if (!sendEmail && data.link?.url) copy(data.link.url, data.link.id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async (link) => {
    const to = (email.trim() || link.instructor_email || '').trim();
    if (!to) { toast.error("Enter the teacher's email address first"); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/rosters/links/${link.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ instructor_email: to }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send');
      toast.success(data.message);
      await fetchLinks();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (link) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/rosters/links/${link.id}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to revoke');
      toast.success('Link turned off — the teacher can no longer open it');
      await fetchLinks();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success('Link copied');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const active = links.filter((l) => !l.revoked);
  const revoked = links.filter((l) => l.revoked);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start md:items-center justify-center z-50 p-4 overflow-y-auto"
      data-testid="share-roster-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl my-8">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200">
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Share Roster with Teacher</h2>
            <p className="text-sm text-stone-500 mt-0.5">{item?.title}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close"
            data-testid="share-roster-close">
            <X size={18} />
          </Button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
            <p>
              Anyone with this link can see student names, ages and guardian contact details for
              <strong> this class only</strong>. It does not expire — keep it for the whole term and
              turn it off here when the teacher no longer needs it.
            </p>
          </div>

          <div>
            <Label htmlFor="roster-teacher-email">Teacher's email</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
              <Input
                id="roster-teacher-email"
                type="email"
                placeholder="teacher@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="roster-teacher-email-input"
              />
              <Button
                className="bg-cafa-red hover:bg-red-900 text-white whitespace-nowrap"
                disabled={busy}
                onClick={() => createLink(true)}
                data-testid="create-and-email-roster-link"
              >
                <Mail size={14} className="mr-1.5" /> Create &amp; Email Link
              </Button>
            </div>
            <button
              type="button"
              className="mt-2 text-sm text-stone-600 underline hover:text-stone-900 inline-flex items-center gap-1.5 disabled:opacity-50"
              disabled={busy}
              onClick={() => createLink(false)}
              data-testid="create-roster-link-only"
            >
              <Link2 size={13} /> Just create a link I can copy myself
            </button>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-stone-800 mb-2">
              Active links {active.length > 0 && `(${active.length})`}
            </h3>
            {loading ? (
              <p className="text-sm text-stone-400">Loading…</p>
            ) : active.length === 0 ? (
              <p className="text-sm text-stone-400" data-testid="no-active-roster-links">
                No roster link yet for this class.
              </p>
            ) : (
              <div className="space-y-2">
                {active.map((link) => (
                  <div key={link.id} className="p-3 border border-stone-200 rounded-lg bg-stone-50"
                    data-testid={`roster-link-${link.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-xs font-mono text-stone-600 break-all">{link.url}</p>
                        <p className="text-xs text-stone-500 mt-1">
                          {link.instructor_email ? <>Sent to {link.instructor_email} · </> : null}
                          Opened {link.access_count || 0}×
                          {link.last_accessed_at && ` · last ${new Date(link.last_accessed_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="outline" size="sm" onClick={() => copy(link.url, link.id)}
                          title="Copy link" aria-label="Copy roster link"
                          data-testid={`copy-roster-link-${link.id}`}>
                          {copiedId === link.id ? <Check size={14} /> : <Copy size={14} />}
                        </Button>
                        <Button variant="outline" size="sm" disabled={busy} onClick={() => resend(link)}
                          title="Email this link to the teacher" aria-label="Email roster link"
                          data-testid={`email-roster-link-${link.id}`}>
                          <Mail size={14} />
                        </Button>
                        <a href={link.url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center px-3 border border-stone-200 rounded-md hover:bg-white"
                          title="Preview roster" aria-label="Preview roster">
                          <ExternalLink size={14} />
                        </a>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                          disabled={busy} onClick={() => revoke(link)}
                          title="Turn off this link" aria-label="Revoke roster link"
                          data-testid={`revoke-roster-link-${link.id}`}>
                          <Ban size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {revoked.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-stone-500 mb-2">Turned off ({revoked.length})</h3>
              <div className="space-y-1">
                {revoked.map((link) => (
                  <p key={link.id} className="text-xs text-stone-400 font-mono break-all line-through">
                    {link.url}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-stone-200 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="share-roster-done">Done</Button>
        </div>
      </div>
    </div>
  );
};
